import { useState } from "react";
import type { InspectionDetails, InspectionResult, Money, Qty, Technician, WorkOrderPartLine } from "@autorepair/contracts";
import { formatQty, parseMoneyInput, parseQtyInput } from "../lib/money";
import { toIsoDate } from "../lib/dateRange";
import {
  ModernButton,
  ModernCheckboxRow,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernModal,
  ModernSelect,
  ModernTextarea,
} from "./ui-modern";
import { useConfirmGate } from "../lib/useConfirm";
import { useShopTypeLabels } from "../lib/theme";

export function EditPartLineModal({
  line,
  shopSuppliesEnabled = true,
  // Current shop partsTaxRateSnapshot — the "Tax exempt" checkbox only
  // shows when this category is actually taxed at all.
  taxRate = "0",
  inspectors = [],
  onSave,
  onClose,
}: {
  line: WorkOrderPartLine;
  /** Whether the tenant has Shop Supplies enabled at all (see
   * tenant_settings.shopSuppliesEnabled) — hides the checkbox below when
   * false. */
  shopSuppliesEnabled?: boolean;
  taxRate?: string;
  /** Technicians offered as the inspector on a state-inspection line. */
  inspectors?: Technician[];
  onSave: (values: {
    partNumber: string;
    description: string;
    quantity: Qty;
    unitListPrice: Money;
    includeInShopSupplies: boolean;
    isTaxable?: boolean;
    inspection?: InspectionDetails;
  }) => Promise<void>;
  onClose: () => void;
}) {
  const [partNumber, setPartNumber] = useState(line.partNumberSnapshot ?? "");
  const [description, setDescription] = useState(line.descriptionSnapshot);
  const [quantity, setQuantity] = useState<string>(formatQty(line.quantity));
  const [price, setPrice] = useState<string>(line.unitListPrice);
  const [includeInShopSupplies, setIncludeInShopSupplies] = useState(line.includeInShopSupplies);
  const [taxExempt, setTaxExempt] = useState(!line.isTaxable);
  // State-inspection details (only used when line.isStateInspection).
  const [result, setResult] = useState<InspectionResult | "">(line.inspection?.result ?? "");
  const [virNumber, setVirNumber] = useState(line.inspection?.virNumber ?? "");
  const [inspectorId, setInspectorId] = useState<string>(line.inspection?.inspectorTechnicianId != null ? String(line.inspection.inspectorTechnicianId) : "");
  const [inspectedOn, setInspectedOn] = useState(line.inspection?.inspectedOn ?? toIsoDate(new Date()));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const confirmGate = useConfirmGate();
  const shopTypeLabels = useShopTypeLabels();
  const showTaxExempt = Number(taxRate) > 0;

  async function save(): Promise<void> {
    if (!partNumber.trim()) return setError("Part is required.");
    const qty = parseQtyInput(quantity);
    if (!qty) return setError('Quantity should look like "1" or "2.5".');
    const unitListPrice = parseMoneyInput(price);
    if (!unitListPrice) return setError('Price should look like "42.50".');
    setError(null);
    setSaving(true);
    try {
      await onSave({
        partNumber: partNumber.trim(),
        description,
        quantity: qty,
        unitListPrice,
        includeInShopSupplies,
        isTaxable: showTaxExempt ? !taxExempt : undefined,
        ...(line.isStateInspection
          ? {
              inspection: {
                result: result || null,
                virNumber: virNumber.trim() || null,
                inspectorTechnicianId: inspectorId ? Number(inspectorId) : null,
                inspectedOn,
              },
            }
          : {}),
      });
      onClose();
    } catch {
      setError("Could not save this part. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    confirmGate.request(() => void save());
  }

  return (
    <ModernModal title={`Edit ${shopTypeLabels.partLabel}`} onCancel={onClose}>
      {error && <ModernErrorBanner message={error} />}
      <form onSubmit={handleSubmit}>
        <ModernField label={shopTypeLabels.partLabel} htmlFor="editPartNumber" required>
          <ModernInput id="editPartNumber" required uppercase value={partNumber} onChange={(e) => setPartNumber(e.target.value)} />
        </ModernField>
        <ModernField label="Description" htmlFor="editPartDescription" required>
          <ModernTextarea id="editPartDescription" required uppercase rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </ModernField>
        <ModernField label="Qty" htmlFor="editPartQty">
          <ModernInput id="editPartQty" decimal value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </ModernField>
        <ModernField label="Price" htmlFor="editPartPrice">
          <ModernInput id="editPartPrice" decimal value={price} onChange={(e) => setPrice(e.target.value)} />
          {!line.isStateInspection && shopSuppliesEnabled && (
            <ModernCheckboxRow className="mt-1.5" checked={includeInShopSupplies} onChange={setIncludeInShopSupplies}>
              Include in Shop Supplies
            </ModernCheckboxRow>
          )}
          {!line.isStateInspection && showTaxExempt && (
            <ModernCheckboxRow className="mt-1.5" checked={taxExempt} onChange={setTaxExempt}>
              Tax exempt
            </ModernCheckboxRow>
          )}
        </ModernField>
        {line.isStateInspection && (
          <div className="mb-3 rounded-[10px] border border-[#bfe9e6] bg-[#f1fbfa] p-3">
            <div className="mb-2 text-[13px] font-bold text-[#071a33]">State inspection details (optional)</div>
            <p className="m-0 mb-2.5 text-[12px] text-[#6b7a8f]">
              Always tax exempt and never charged shop supplies. These print on the invoice and appear on the Inspections page.
            </p>
            <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
              <ModernField label="Result" htmlFor="editInspectionResult">
                <ModernSelect id="editInspectionResult" value={result} onChange={(e) => setResult(e.target.value as InspectionResult | "")}>
                  <option value="">Not entered yet</option>
                  <option value="pass">Pass</option>
                  <option value="fail">Fail</option>
                </ModernSelect>
              </ModernField>
              <ModernField label="Inspection date" htmlFor="editInspectionDate" required>
                <ModernInput id="editInspectionDate" type="date" required value={inspectedOn} onChange={(e) => setInspectedOn(e.target.value)} />
              </ModernField>
              <ModernField label="VIR / test record #" htmlFor="editInspectionVir">
                <ModernInput id="editInspectionVir" maxLength={60} value={virNumber} onChange={(e) => setVirNumber(e.target.value)} />
              </ModernField>
              <ModernField label="Inspector" htmlFor="editInspectionInspector">
                <ModernSelect id="editInspectionInspector" value={inspectorId} onChange={(e) => setInspectorId(e.target.value)}>
                  <option value="">Not entered</option>
                  {inspectors.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.displayName}
                    </option>
                  ))}
                </ModernSelect>
              </ModernField>
            </div>
          </div>
        )}
        <div className="flex gap-2.5">
          <ModernButton type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </ModernButton>
          <ModernButton type="button" variant="secondary" onClick={onClose}>
            Cancel
          </ModernButton>
        </div>
      </form>
      <ModernConfirmDialog {...confirmGate.dialogProps} message={`Save these changes to the ${shopTypeLabels.partLabel.toLowerCase()}?`} />
    </ModernModal>
  );
}
